import { assertCalendarIcsText } from '@streamlain/calendar-ics-security';
import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds';
import ICAL from 'ical.js';

import { dataViewI18nT } from '../../core/i18n.js';

export type CalendarAttendeeStatus =
  | 'accepted'
  | 'declined'
  | 'needs_action'
  | 'tentative';

export type CalendarParsedAttendee = {
  email: string | null;
  key: string;
  name: string | null;
  status: CalendarAttendeeStatus;
};

export type CalendarEventLike = {
  allDay: boolean;
  attendees: string;
  calendarId: string;
  description: string;
  endAt: Date;
  locationPhysical: string;
  locationVirtual: string;
  recurrence: string;
  recurrenceExdate: string;
  recurrenceId: string;
  reminder: string;
  rowId: string;
  startAt: Date;
  status: string;
  timezone: string;
  title: string;
  viewId?: string;
};

export type CalendarOccurrence<
  T extends CalendarEventLike = CalendarEventLike,
> = {
  endAt: Date;
  isRecurring: boolean;
  occurrenceKey: string;
  originalStartAt: Date;
  source: T;
  startAt: Date;
};

type WallDate = {
  day: number;
  month: number;
  year: number;
};

type WallDateTime = WallDate & {
  hour: number;
  millisecond: number;
  minute: number;
  second: number;
};

export class CalendarRecurrenceRuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CalendarRecurrenceRuleError';
  }
}

const MAX_OCCURRENCES = 720;
const MAX_RECURRENCE_ITERATIONS = 8000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function expandCalendarOccurrences<T extends CalendarEventLike>(
  event: T,
  windowStart: Date,
  windowEnd: Date
): CalendarOccurrence<T>[] {
  const rule = parseRRule(event.recurrence);
  if (!rule) {
    return calendarEventOverlaps(
      event,
      event.startAt,
      event.endAt,
      windowStart,
      windowEnd
    )
      ? [toOccurrence(event, event.startAt, event.endAt, false)]
      : [];
  }

  const timezone = normalizeTimeZone(event.timezone);
  const exdates = parseExdates(event.recurrenceExdate, timezone);
  const duration = Math.max(
    0,
    differenceInMilliseconds(event.endAt, event.startAt)
  );
  const simpleOccurrences = expandSimpleIntervalOccurrences(
    event,
    rule,
    timezone,
    exdates,
    duration,
    windowStart,
    windowEnd
  );
  if (simpleOccurrences) return simpleOccurrences;

  const result: CalendarOccurrence<T>[] = [];
  const eventStartWall = zonedDateTimeParts(event.startAt, timezone);
  const iterator = rule.iterator(wallDateTimeToIcalTime(eventStartWall));
  const iterationGuard = recurrenceIterationGuard(
    event.startAt,
    windowEnd,
    MAX_OCCURRENCES
  );
  let scanned = 0;

  while (result.length < MAX_OCCURRENCES && scanned < iterationGuard) {
    const next = iterator.next();
    if (!next) break;
    scanned += 1;

    const start = icalTimeToZonedDate(next, timezone);
    if (start > windowEnd) break;

    const end = new Date(start.getTime() + duration);
    const occurrenceKey = occurrenceDateKey(start);
    if (
      !exdates.has(occurrenceKey) &&
      calendarEventOverlaps(event, start, end, windowStart, windowEnd)
    ) {
      result.push(toOccurrence(event, start, end, true));
    }
  }

  return result;
}

function expandSimpleIntervalOccurrences<T extends CalendarEventLike>(
  event: T,
  rule: ICAL.Recur,
  timezone: string,
  exdates: ReadonlySet<string>,
  duration: number,
  windowStart: Date,
  windowEnd: Date
): CalendarOccurrence<T>[] | null {
  if (rule.freq !== 'DAILY' && rule.freq !== 'WEEKLY') return null;
  if (Object.keys(rule.parts).length > 0) return null;

  const intervalDays =
    (rule.freq === 'WEEKLY' ? 7 : 1) * Math.max(1, rule.interval || 1);
  const eventStartWall = zonedDateTimeParts(event.startAt, timezone);
  const windowStartWall = zonedDateTimeParts(windowStart, timezone);
  const dayDelta =
    wallDateOrdinal(windowStartWall) - wallDateOrdinal(eventStartWall);
  const count = rule.count ?? Number.POSITIVE_INFINITY;
  const until = rule.until?.toJSDate();
  const result: CalendarOccurrence<T>[] = [];
  let occurrenceIndex = Math.max(0, Math.floor(dayDelta / intervalDays) - 2);

  while (
    occurrenceIndex < count &&
    result.length < MAX_OCCURRENCES &&
    occurrenceIndex < Number.MAX_SAFE_INTEGER
  ) {
    const start = zonedWallDateTimeToDate(
      addDaysToWallDateTime(eventStartWall, occurrenceIndex * intervalDays),
      timezone
    );
    if (until && start > until) break;
    if (start > windowEnd) break;

    const end = new Date(start.getTime() + duration);
    const occurrenceKey = occurrenceDateKey(start);
    if (
      !exdates.has(occurrenceKey) &&
      calendarEventOverlaps(event, start, end, windowStart, windowEnd)
    ) {
      result.push(toOccurrence(event, start, end, true));
    }
    occurrenceIndex += 1;
  }

  return result;
}

function wallDateOrdinal(wall: WallDate): number {
  return Math.floor(Date.UTC(wall.year, wall.month - 1, wall.day) / MS_PER_DAY);
}

function addDaysToWallDateTime(wall: WallDateTime, days: number): WallDateTime {
  const date = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days));
  return {
    ...wall,
    day: date.getUTCDate(),
    month: date.getUTCMonth() + 1,
    year: date.getUTCFullYear(),
  };
}

function wallDateTimeToIcalTime(wall: WallDateTime): ICAL.Time {
  return ICAL.Time.fromData({
    day: wall.day,
    hour: wall.hour,
    isDate: false,
    minute: wall.minute,
    month: wall.month,
    second: wall.second,
    year: wall.year,
  });
}

function icalTimeToZonedDate(time: ICAL.Time, timezone: string): Date {
  return zonedWallDateTimeToDate(
    {
      day: time.day,
      hour: time.isDate ? 0 : time.hour,
      millisecond: 0,
      minute: time.isDate ? 0 : time.minute,
      month: time.month,
      second: time.isDate ? 0 : time.second,
      year: time.year,
    },
    timezone
  );
}

function recurrenceIterationGuard(
  eventStart: Date,
  windowEnd: Date,
  maxOccurrences: number
): number {
  const spanDays = Math.max(
    1,
    Math.ceil((windowEnd.getTime() - eventStart.getTime()) / MS_PER_DAY)
  );
  return Math.min(
    MAX_RECURRENCE_ITERATIONS,
    Math.max(maxOccurrences * 8, spanDays + 366, 3660)
  );
}

export function parseCalendarAttendees(
  value: string
): CalendarParsedAttendee[] {
  return splitCalendarAttendees(value)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const statusMatch = part.match(
        /\[(accepted|declined|tentative|needs[-_ ]action)\]$/i
      );
      const status = normalizeStatus(statusMatch?.[1]);
      const cleanPart = statusMatch
        ? part.slice(0, statusMatch.index).trim()
        : part;
      const angleMatch = cleanPart.match(/^(.*)<([^>]+)>$/);
      const rawEmail =
        angleMatch?.[2] ?? cleanPart.match(/[^\s<>]+@[^\s<>]+/)?.[0];
      const email = rawEmail?.trim().replace(/^mailto:/i, '') || null;
      const name =
        (angleMatch?.[1] ?? cleanPart.replace(rawEmail ?? '', '')).trim() ||
        null;
      const key = (email ?? name ?? cleanPart).toLowerCase();
      return {
        email,
        key,
        name,
        status,
      };
    });
}

function splitCalendarAttendees(value: string): string[] {
  const parts: string[] = [];
  let current = '';
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    if (char === '\n') {
      pushAttendeePart(parts, current);
      current = '';
      continue;
    }
    if (
      (char === ',' || char === ';') &&
      shouldSplitAttendeeDelimiter(value, index, current)
    ) {
      pushAttendeePart(parts, current);
      current = '';
      continue;
    }
    current += char;
  }
  pushAttendeePart(parts, current);
  return parts;
}

function pushAttendeePart(parts: string[], value: string): void {
  const trimmed = value.trim();
  if (trimmed) {
    parts.push(trimmed);
  }
}

function hasAttendeeEmail(value: string): boolean {
  return /[^\s<>]+@[^\s<>]+/.test(value);
}

function shouldSplitAttendeeDelimiter(
  value: string,
  delimiterIndex: number,
  current: string
): boolean {
  if (hasAttendeeEmail(current)) return true;
  return !nextAttendeeSegmentHasAngleEmail(value, delimiterIndex + 1);
}

function nextAttendeeSegmentHasAngleEmail(
  value: string,
  startIndex: number
): boolean {
  const nextDelimiterIndex = findNextAttendeeDelimiterIndex(value, startIndex);
  const segment =
    nextDelimiterIndex === -1
      ? value.slice(startIndex)
      : value.slice(startIndex, nextDelimiterIndex);
  return /<[^<>@\s]+@[^<>\s]+>/.test(segment);
}

function findNextAttendeeDelimiterIndex(
  value: string,
  startIndex: number
): number {
  for (let index = startIndex; index < value.length; index += 1) {
    const char = value[index];
    if (char === '\n' || char === ',' || char === ';') {
      return index;
    }
  }
  return -1;
}

export function findCalendarConflicts<T extends CalendarEventLike>(
  event: CalendarOccurrence<T>,
  events: readonly CalendarOccurrence<T>[]
): CalendarOccurrence<T>[] {
  const attendeeKeys = new Set(
    parseCalendarAttendees(event.source.attendees).map(attendee => attendee.key)
  );
  if (!attendeeKeys.size) return [];
  return events.filter(candidate => {
    if (candidate.occurrenceKey === event.occurrenceKey) return false;
    if (
      !overlaps(event.startAt, event.endAt, candidate.startAt, candidate.endAt)
    ) {
      return false;
    }
    return parseCalendarAttendees(candidate.source.attendees).some(attendee =>
      attendeeKeys.has(attendee.key)
    );
  });
}

export function renderCalendarIcs<T extends CalendarEventLike>(
  events: readonly T[],
  method: 'PUBLISH' | 'REQUEST' | 'CANCEL' = 'PUBLISH'
): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//StreamLain//Database Calendar//EN',
    `METHOD:${method}`,
    ...events.flatMap(event => renderEventIcs(event, method)),
    'END:VCALENDAR',
  ];
  return `${lines.join('\r\n')}\r\n`;
}

export type ParsedIcsEvent = {
  allDay: boolean;
  attendees: string;
  calendarId: string;
  description: string;
  endAt: Date;
  locationPhysical: string;
  locationVirtual: string;
  recurrence: string;
  recurrenceExdate: string;
  recurrenceId: string;
  startAt: Date;
  status: string;
  timezone: string;
  title: string;
};

export function parseCalendarIcs(text: string): ParsedIcsEvent[] {
  assertCalendarIcsText(text);
  const unfolded = text.replace(/\r?\n[ \t]/g, '');
  const blocks = unfolded.match(/BEGIN:VEVENT[\s\S]*?END:VEVENT/g) ?? [];
  return blocks.flatMap(block => {
    const props = parseIcsProps(block);
    const startRaw = propString(props.DTSTART);
    const startTimezone = propString(props.DTSTART_TZID);
    const endTimezone = propString(props.DTEND_TZID) ?? startTimezone;
    if (!startRaw) return [];
    const allDay = startRaw.length === 8;
    const startAt = parseIcsDate(startRaw, startTimezone);
    if (!startAt) return [];
    const endAt = normalizeParsedIcsEndAt(
      startAt,
      parseIcsDate(propString(props.DTEND), endTimezone),
      allDay
    );
    const attendees = propList(props.ATTENDEE_LIST)
      .map(normalizeAttendeeIcsValue)
      .join('\n');
    const recurrence = validateCalendarRecurrenceRule(
      propString(props.RRULE) ?? ''
    );
    return [
      {
        allDay,
        attendees,
        calendarId: propString(props.CATEGORIES) ?? '',
        description: propString(props.DESCRIPTION) ?? '',
        endAt,
        locationPhysical: propString(props.LOCATION) ?? '',
        locationVirtual: propString(props.URL) ?? '',
        recurrence: recurrence ?? '',
        recurrenceExdate: propList(props.EXDATE).join('\n'),
        recurrenceId: propString(props.RECURRENCE_ID) ?? '',
        startAt,
        status: propString(props.STATUS) ?? '',
        timezone:
          startTimezone ??
          endTimezone ??
          propString(props.EXDATE_TZID) ??
          propString(props.TZID) ??
          'UTC',
        title:
          propString(props.SUMMARY) ??
          dataViewI18nT('calendar.event.importedTitle'),
      },
    ];
  });
}

export function appendRecurrenceException(
  existing: string,
  occurrenceStart: Date
): string {
  const key = occurrenceStart.toISOString();
  const current = existing
    .split(/[,\n;]/)
    .map(value => value.trim())
    .filter(Boolean);
  if (
    current.some(
      value =>
        occurrenceDateKey(new Date(value)) ===
        occurrenceDateKey(occurrenceStart)
    )
  ) {
    return existing;
  }
  return [...current, key].join('\n');
}

function toOccurrence<T extends CalendarEventLike>(
  event: T,
  startAt: Date,
  endAt: Date,
  isRecurring: boolean
): CalendarOccurrence<T> {
  return {
    endAt,
    isRecurring,
    occurrenceKey: `${event.rowId}:${occurrenceDateKey(startAt)}`,
    originalStartAt: new Date(startAt),
    source: event,
    startAt,
  };
}

function parseRRule(value: string): ICAL.Recur | null {
  const normalized = validateCalendarRecurrenceRule(value);
  if (!normalized) return null;
  return ICAL.Recur.fromString(normalized);
}

export function validateCalendarRecurrenceRule(value: string): string | null {
  const normalized = value.trim().replace(/^RRULE:/i, '');
  if (!normalized) return null;
  const pairs = parseRRulePairs(normalized);
  if (!isFrequency(pairs.FREQ?.toUpperCase())) {
    throw invalidRRule(dataViewI18nT('calendar.error.recurrenceFreqInvalid'));
  }
  if (pairs.INTERVAL && !/^[1-9]\d*$/.test(pairs.INTERVAL)) {
    throw invalidRRule(
      dataViewI18nT('calendar.error.recurrenceIntervalInvalid')
    );
  }
  if (pairs.COUNT && !/^[1-9]\d*$/.test(pairs.COUNT)) {
    throw invalidRRule(dataViewI18nT('calendar.error.recurrenceCountInvalid'));
  }
  if (pairs.BYMONTHDAY) {
    for (const item of pairs.BYMONTHDAY.split(',')) {
      const value = Number.parseInt(item.trim(), 10);
      if (
        !Number.isInteger(value) ||
        value === 0 ||
        value < -31 ||
        value > 31
      ) {
        throw invalidRRule(
          dataViewI18nT('calendar.error.recurrenceMonthDayInvalid')
        );
      }
    }
  }
  try {
    ICAL.Recur.fromString(normalized);
  } catch (error) {
    throw invalidRRule(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.recurrenceRuleInvalid')
    );
  }
  return normalized;
}

function parseRRulePairs(value: string): Record<string, string> {
  return Object.fromEntries(
    value.split(';').flatMap(part => {
      const separator = part.indexOf('=');
      if (separator < 1) return [];
      const key = part.slice(0, separator).trim().toUpperCase();
      const item = part.slice(separator + 1).trim();
      return key && item ? ([[key, item]] as const) : [];
    })
  );
}

function invalidRRule(reason: string): CalendarRecurrenceRuleError {
  return new CalendarRecurrenceRuleError(
    `${dataViewI18nT('calendar.error.recurrenceInvalid')}: ${reason}`
  );
}

function normalizeTimeZone(value: string): string {
  const timezone = value.trim() || 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(
      new Date(0)
    );
    return timezone;
  } catch {
    return 'UTC';
  }
}

function zonedDateTimeParts(date: Date, timeZone: string): WallDateTime {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date);
  const byType = new Map(parts.map(part => [part.type, part.value]));
  return {
    day: numberPart(byType, 'day'),
    hour: numberPart(byType, 'hour'),
    millisecond: date.getMilliseconds(),
    minute: numberPart(byType, 'minute'),
    month: numberPart(byType, 'month'),
    second: numberPart(byType, 'second'),
    year: numberPart(byType, 'year'),
  };
}

function numberPart(parts: ReadonlyMap<string, string>, type: string): number {
  return Number.parseInt(parts.get(type) ?? '0', 10);
}

function zonedWallDateTimeToDate(wall: WallDateTime, timeZone: string): Date {
  const targetUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    wall.millisecond
  );
  let guessUtc = targetUtc;

  for (let index = 0; index < 4; index += 1) {
    const actual = zonedDateTimeParts(new Date(guessUtc), timeZone);
    const actualUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
      actual.millisecond
    );
    const delta = targetUtc - actualUtc;
    if (delta === 0) break;
    guessUtc += delta;
  }

  return new Date(guessUtc);
}

function isFrequency(value: string | undefined): boolean {
  return (
    value === 'DAILY' ||
    value === 'WEEKLY' ||
    value === 'MONTHLY' ||
    value === 'YEARLY'
  );
}

function parseExdates(value: string, timezone: string): Set<string> {
  return new Set(
    value
      .split(/[,\n;]/)
      .map(part => parseIcsDate(part.trim(), timezone) ?? new Date(part.trim()))
      .filter(date => Number.isFinite(date.getTime()))
      .map(date => occurrenceDateKey(date))
  );
}

function overlaps(startA: Date, endA: Date, startB: Date, endB: Date): boolean {
  return startA < endB && endA > startB;
}

function calendarEventOverlaps<T extends CalendarEventLike>(
  event: T,
  startAt: Date,
  endAt: Date,
  windowStart: Date,
  windowEnd: Date
): boolean {
  if (!event.allDay) return overlaps(startAt, endAt, windowStart, windowEnd);
  const inclusiveEnd = endAt < startAt ? startAt : endAt;
  return overlaps(
    startAt,
    addCalendarDays(inclusiveEnd, 1),
    windowStart,
    windowEnd
  );
}

function occurrenceDateKey(date: Date): string {
  return date.toISOString();
}

function normalizeStatus(value: string | undefined): CalendarAttendeeStatus {
  const normalized = value
    ?.toLowerCase()
    .replaceAll('-', '_')
    .replaceAll(' ', '_');
  if (
    normalized === 'accepted' ||
    normalized === 'declined' ||
    normalized === 'tentative' ||
    normalized === 'needs_action'
  ) {
    return normalized;
  }
  return 'needs_action';
}

function renderEventIcs<T extends CalendarEventLike>(
  event: T,
  method: 'PUBLISH' | 'REQUEST' | 'CANCEL'
): string[] {
  return [
    'BEGIN:VEVENT',
    `UID:${escapeIcs(`${event.rowId}-${event.viewId ?? 'calendar'}@streamlain-database-calendar`)}`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DTSTART${event.allDay ? ';VALUE=DATE' : ''}:${formatIcsEventDate(event.startAt, event.allDay)}`,
    `DTEND${event.allDay ? ';VALUE=DATE' : ''}:${formatIcsEventEndDate(event.endAt, event.allDay)}`,
    event.recurrenceId
      ? `RECURRENCE-ID${event.allDay ? ';VALUE=DATE' : ''}:${formatIcsEventDate(new Date(event.recurrenceId), event.allDay)}`
      : '',
    event.calendarId ? `CATEGORIES:${escapeIcs(event.calendarId)}` : '',
    event.locationPhysical
      ? `LOCATION:${escapeIcs(event.locationPhysical)}`
      : '',
    event.locationVirtual ? `URL:${escapeIcs(event.locationVirtual)}` : '',
    event.description ? `DESCRIPTION:${escapeIcs(event.description)}` : '',
    event.recurrence
      ? `RRULE:${validateCalendarRecurrenceRule(event.recurrence)}`
      : '',
    event.recurrenceExdate
      ? `EXDATE:${event.recurrenceExdate
          .split(/[,\n;]/)
          .map(value => value.trim())
          .filter(Boolean)
          .map(value => parseIcsDate(value) ?? new Date(value))
          .filter(date => Number.isFinite(date.getTime()))
          .map(date => formatIcsEventDate(date, event.allDay))
          .join(',')}`
      : '',
    ...parseCalendarAttendees(event.attendees).flatMap(attendee =>
      attendee.email
        ? [
            `ATTENDEE${attendee.name ? `;CN=${escapeIcs(attendee.name)}` : ''};PARTSTAT=${attendee.status.toUpperCase().replaceAll('_', '-')};ROLE=REQ-PARTICIPANT;RSVP=TRUE:mailto:${attendee.email}`,
          ]
        : []
    ),
    method === 'CANCEL'
      ? 'STATUS:CANCELLED'
      : event.status
        ? `STATUS:${escapeIcs(event.status)}`
        : '',
    'END:VEVENT',
  ].filter(Boolean);
}

function formatIcsEventDate(date: Date, allDay: boolean): string {
  if (allDay) return formatAllDayDate(date);
  return formatIcsDate(date);
}

function formatIcsEventEndDate(date: Date, allDay: boolean): string {
  return formatIcsEventDate(allDay ? addCalendarDays(date, 1) : date, allDay);
}

function normalizeParsedIcsEndAt(
  startAt: Date,
  endAt: Date | null,
  allDay: boolean
): Date {
  if (!endAt) return startAt;
  if (!allDay) return endAt;
  const inclusiveEndAt = addCalendarDays(endAt, -1);
  return inclusiveEndAt < startAt ? startAt : inclusiveEndAt;
}

function addCalendarDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function formatAllDayDate(date: Date): string {
  const year = date.getUTCFullYear().toString().padStart(4, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  return `${year}${month}${day}`;
}

function formatIcsDate(date: Date): string {
  return date
    .toISOString()
    .replaceAll('-', '')
    .replaceAll(':', '')
    .replace(/\.\d{3}Z$/, 'Z');
}

function parseIcsDate(
  value: string | undefined,
  timezone = 'UTC'
): Date | null {
  if (!value) return null;
  if (/^\d{8}$/.test(value)) {
    const year = Number.parseInt(value.slice(0, 4), 10);
    const month = Number.parseInt(value.slice(4, 6), 10) - 1;
    const day = Number.parseInt(value.slice(6, 8), 10);
    return new Date(Date.UTC(year, month, day));
  }
  if (/^\d{8}T\d{6}Z?$/.test(value)) {
    if (!value.endsWith('Z')) {
      return zonedWallDateTimeToDate(
        {
          day: Number.parseInt(value.slice(6, 8), 10),
          hour: Number.parseInt(value.slice(9, 11), 10),
          millisecond: 0,
          minute: Number.parseInt(value.slice(11, 13), 10),
          month: Number.parseInt(value.slice(4, 6), 10),
          second: Number.parseInt(value.slice(13, 15), 10),
          year: Number.parseInt(value.slice(0, 4), 10),
        },
        timezone
      );
    }
    const iso = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(9, 11)}:${value.slice(11, 13)}:${value.slice(13, 15)}${value.endsWith('Z') ? 'Z' : ''}`;
    const date = new Date(iso);
    return Number.isFinite(date.getTime()) ? date : null;
  }
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function parseIcsProps(block: string): Record<string, string | string[]> {
  const props: Record<string, string | string[]> = {};
  for (const line of block.split(/\r?\n/)) {
    const separator = line.indexOf(':');
    if (separator < 0) continue;
    const rawKey = line.slice(0, separator);
    const key = rawKey.split(';')[0]?.toUpperCase();
    const normalizedKey = key === 'RECURRENCE-ID' ? 'RECURRENCE_ID' : key;
    const value = unescapeIcs(line.slice(separator + 1));
    if (!normalizedKey || normalizedKey === 'BEGIN' || normalizedKey === 'END')
      continue;
    if (rawKey.includes('TZID=')) {
      const timezone = normalizeIcsParam(rawKey.match(/TZID=([^;:]+)/)?.[1]);
      props.TZID = timezone;
      props[`${normalizedKey}_TZID`] = timezone;
    }
    if (normalizedKey === 'ATTENDEE') {
      props.ATTENDEE_LIST = [
        ...((props.ATTENDEE_LIST as string[]) ?? []),
        line,
      ];
    } else {
      appendIcsProp(props, normalizedKey, value);
    }
  }
  return props;
}

function appendIcsProp(
  props: Record<string, string | string[]>,
  key: string,
  value: string
): void {
  const current = props[key];
  if (!current) {
    props[key] = value;
    return;
  }
  props[key] = [...propList(current), value];
}

function propString(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function propList(value: string | string[] | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function normalizeIcsParam(value: string | undefined): string {
  return value?.trim().replace(/^"|"$/g, '') ?? '';
}

function normalizeAttendeeIcsValue(value: string): string {
  const separator = value.indexOf(':');
  const rawKey = separator >= 0 ? value.slice(0, separator) : '';
  const rawValue = separator >= 0 ? value.slice(separator + 1) : value;
  const email = unescapeIcs(rawValue)
    .trim()
    .replace(/^mailto:/i, '');
  if (!email) return '';
  const name = normalizeAttendeeIcsParam(rawKey, 'CN');
  const statusValue = normalizeAttendeeIcsParam(rawKey, 'PARTSTAT');
  const identity = name ? `${name} <${email}>` : email;
  return statusValue
    ? `${identity} [${normalizeStatus(statusValue)}]`
    : identity;
}

function normalizeAttendeeIcsParam(
  rawKey: string,
  name: 'CN' | 'PARTSTAT'
): string | null {
  for (const part of rawKey.split(';').slice(1)) {
    const separator = part.indexOf('=');
    if (separator < 0) continue;
    if (part.slice(0, separator).trim().toUpperCase() !== name) continue;
    const value = normalizeIcsParam(part.slice(separator + 1));
    return unescapeIcs(value) || null;
  }
  return null;
}

function escapeIcs(value: string): string {
  return value
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll('\n', '\\n')
    .replaceAll('\r', '');
}

function unescapeIcs(value: string): string {
  return value
    .replaceAll('\\n', '\n')
    .replaceAll('\\,', ',')
    .replaceAll('\\;', ';')
    .replaceAll('\\\\', '\\');
}
